import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useCarrierOrganizations, useSelectCarrierOrganization } from '@/api/domains/carriers';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { usePageTitle } from '@/components/layout/page-title';
import { toApiProblem, toastProblem } from '@/lib/errors';

export function SelectInPostOrganizationPage() {
  const { t } = useTranslation('carriers');
  const tCommon = useTranslation('common').t;
  usePageTitle(t('connect.selectOrganization.title'));
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const orgsQuery = useCarrierOrganizations('inpost');
  const selectMutation = useSelectCarrierOrganization('inpost');

  const organizations = orgsQuery.data ?? [];
  const activeOrganizations = organizations.filter((org) => org.status === 'ACTIVE');

  const handleSubmit = () => {
    if (!selectedId) return;
    selectMutation.mutate(
      { organization_id: selectedId },
      {
        onSuccess: () => {
          toast.success(t('connect.selectOrganization.saved'));
          navigate('/authorization', { replace: true });
        },
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="space-y-2">
        <h2 className="text-4xl font-medium tracking-tight">
          {t('connect.selectOrganization.title')}
        </h2>
        <p className="text-base text-muted-foreground">
          {t('connect.selectOrganization.subtitle')}
        </p>
      </div>

      {orgsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('connect.selectOrganization.loading')}</p>
      ) : orgsQuery.isError ? (
        <p className="text-sm text-destructive">{t('connect.selectOrganization.loadError')}</p>
      ) : activeOrganizations.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('connect.selectOrganization.empty')}</p>
      ) : (
        <RadioGroup
          name="inpost-organization"
          aria-label="InPost organizations"
          value={selectedId}
          onValueChange={setSelectedId}
        >
          {activeOrganizations.map((org) => (
            <RadioGroupItem
              key={org.id}
              value={org.id}
              title={org.name || org.id}
              description={[org.type, org.country].filter(Boolean).join(' · ') || org.id}
            />
          ))}
        </RadioGroup>
      )}

      <div className="flex items-center justify-end gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate('/authorization')}
          disabled={selectMutation.isPending}
        >
          {tCommon('actions.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedId || selectMutation.isPending}
          className="rounded-[10px]"
        >
          {selectMutation.isPending
            ? t('connect.selectOrganization.saving')
            : t('connect.selectOrganization.use')}
        </Button>
      </div>
    </div>
  );
}
